/**
 * "핵심 정보" — the few facts that make one product page different from its
 * siblings (10-14 content audit C1·C3, template §4). Every value comes from
 * the product's own DB rows; a fact without data is left out, never guessed.
 *
 * Two steps, both pure:
 *  1. `buildKeyFacts(product)` → locale-free facts (numbers, keys, and text the
 *     detail RPC already localized: inclusions, section items, branch names).
 *  2. `formatKeyFacts(facts, t, formatPrice)` → label/value rows. The page
 *     formats prices with the viewer's currency (`formatWithCodeFromSource`);
 *     metadata and JSON-LD format in the product currency, which is also what
 *     the server HTML shows before hydration.
 */

import { bikeLicenseText } from "@/lib/mobility/bike-classes";
import { BIKE_CLASSES, bikeClassOf, type BikeClassKey } from "@/lib/mobility/bike-classes";
import { isBikeSpecifications } from "@/lib/mobility/bike-specs";
import { getOneWayReturnOption } from "@/lib/mobility/one-way-return";
import { resolvePartySize } from "@/lib/mobility/camp-party";
import type { ProductOption } from "@/types";
import {
  commonComponentItems,
  disputedComponentNames,
  parseComponentSections,
} from "./component-sections";

export type PickupMode = "keybox24" | "keybox" | "campsite";

export type KeyFact =
  | { key: "license"; classKey: BikeClassKey }
  | { key: "mileage"; km: number }
  | { key: "pickup"; mode: PickupMode }
  | { key: "oneWay"; price: number; unit: string }
  | { key: "branches"; count: number; campsite: boolean }
  | { key: "party"; size: number }
  | { key: "components"; items: string[]; seasonal: boolean }
  | { key: "places"; names: string[]; total: number };

export type KeyFactKey = KeyFact["key"];

export type KeyFactCategory = "bike" | "camping" | "gear";

interface BranchLike {
  id: string;
  name?: string | null;
  notes?: string | null;
}

/** The slice of `ProductDetail` the facts read. */
export interface KeyFactsInput {
  name?: string | null;
  category_code?: string | null;
  tags?: string[] | null;
  specifications?: Record<string, unknown> | null;
  inclusions?: string[] | null;
  options?: ProductOption[] | null;
  branches?: readonly BranchLike[] | null;
  detail_sections?: ReadonlyArray<{ description?: string | null; display_order?: number | null }> | null;
}

/** Smartphones (20-01), cameras (10-01) and beauty devices (30-01). */
const GEAR_PREFIXES = ["20-01", "10-01", "30-01"];

export function keyFactCategory(product: KeyFactsInput): KeyFactCategory | null {
  if (isBikeSpecifications(product.specifications)) return "bike";
  const code = product.category_code ?? "";
  if (code.startsWith("10-04")) return "camping";
  if (GEAR_PREFIXES.some((p) => code === p || code.startsWith(`${p}-`))) return "gear";
  return null;
}

// ---------------------------------------------------------------------------
// Bike
// ---------------------------------------------------------------------------

const KNOWN_CLASSES = new Set<string>(BIKE_CLASSES.map((c) => c.key));
/** Classes whose mileage allowance is the "125cc and under" figure. */
const SMALL_CLASSES = new Set<BikeClassKey>(["moped", "moped2"]);
const BIG_CLASSES = new Set<BikeClassKey>(["c250", "c400", "large", "premium"]);

export function bikeClassKeyOf(product: KeyFactsInput): BikeClassKey | null {
  const spec = product.specifications?.classKey;
  if (typeof spec === "string" && KNOWN_CLASSES.has(spec)) return spec as BikeClassKey;
  const tagged = bikeClassOf({ tags: product.tags });
  return tagged === "unknown" ? null : tagged;
}

const KM = /(\d[\d,]*)\s*km/i;
const SMALL_CC = /125\s*cc/i;
const BIG_CC = /126\s*cc/i;

/**
 * Mileage included up to 24 hours, read from the partner's inclusion line
 * (all 5 languages share the shape):
 *   "기준 주행거리 (126cc 이상: 24시간까지 400km · 48시간까지 600km …)"
 *   "기준 주행거리 (24시간까지: 125cc 이하 80km / 126cc 이상 400km)"
 * The first figure of a single-class line is the 24-hour one. A line that
 * names the other class, or a class we can't place (trike), gives null.
 */
export function parseMileage24h(
  inclusions: readonly string[] | null | undefined,
  classKey: BikeClassKey | null,
): number | null {
  if (!classKey) return null;
  const small = SMALL_CLASSES.has(classKey);
  const big = BIG_CLASSES.has(classKey);
  if (!small && !big) return null;
  for (const line of inclusions ?? []) {
    if (!KM.test(line) || !/24/.test(line)) continue;
    const hasSmall = SMALL_CC.test(line);
    const hasBig = BIG_CC.test(line);
    let segment: string | null = null;
    if (hasSmall && hasBig) {
      // "125cc 이하 80km / 126cc 이상 400km" · "80 km for 125cc … / 400 km for 126cc …"
      const marker = small ? SMALL_CC : BIG_CC;
      segment = line.split(/[/／]/).find((part) => marker.test(part)) ?? null;
    } else if ((hasSmall && small) || (hasBig && big)) {
      segment = line;
    }
    if (segment === null) continue;
    const km = KM.exec(segment);
    if (!km) continue;
    const value = Number(km[1].replace(/,/g, ""));
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

const KEYBOX = /키박스|キーボックス|key\s?box|钥匙盒|鑰匙盒/i;
const ALL_DAY = /24\s*(?:시간|時間|小时|小時|h\b|hours?)|24\s*\/\s*7/i;
const CAMPSITE_DESK =
  /캠핑장\s*접수처|キャンプ場の受付|campsite reception|露营地接待处|露營地接待處/i;

/**
 * How the item is handed over, when every branch's note says the same thing.
 * One branch without the note (or with another method) → null.
 */
export function pickupModeOf(branches: readonly BranchLike[] | null | undefined): PickupMode | null {
  const list = branches ?? [];
  if (list.length === 0) return null;
  const notes = list.map((b) => b.notes ?? "");
  if (notes.every((n) => CAMPSITE_DESK.test(n))) return "campsite";
  if (notes.every((n) => KEYBOX.test(n))) {
    return notes.every((n) => ALL_DAY.test(n)) ? "keybox24" : "keybox";
  }
  return null;
}

function uniqueBranchCount(branches: readonly BranchLike[] | null | undefined): number {
  return new Set((branches ?? []).map((b) => b.id)).size;
}

// ---------------------------------------------------------------------------
// Components (camping sets, gear)
// ---------------------------------------------------------------------------

const MAX_ITEMS = 6;
const MAX_ITEM_CHARS = 40;
/** A sentence ("구성품은 … 확인해 주세요.") is an instruction, not an item. */
const SENTENCE_END = /[.。!！?？]$/;

export function inclusionItems(inclusions: readonly string[] | null | undefined): string[] {
  return (inclusions ?? [])
    .map((line) => line.trim())
    .filter((line) => line && [...line].length <= MAX_ITEM_CHARS && !SENTENCE_END.test(line))
    .slice(0, MAX_ITEMS);
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

/** Minimum rows for a non-bike block — a single line repeats the section below it. */
export const MIN_BLOCK_ROWS = 2;
const MAX_PLACES = 3;

export function buildKeyFacts(product: KeyFactsInput): KeyFact[] {
  const category = keyFactCategory(product);
  if (!category) return [];
  const facts: KeyFact[] = [];
  const branchCount = uniqueBranchCount(product.branches);
  const pickup = pickupModeOf(product.branches);

  if (category === "bike") {
    const classKey = bikeClassKeyOf(product);
    if (classKey) facts.push({ key: "license", classKey });
    const km = parseMileage24h(product.inclusions, classKey);
    if (km !== null) facts.push({ key: "mileage", km });
    if (pickup === "keybox24" || pickup === "keybox") facts.push({ key: "pickup", mode: pickup });
    const oneWay = getOneWayReturnOption({
      specifications: product.specifications,
      options: product.options ?? [],
    });
    if (oneWay && oneWay.choice.price > 0) {
      facts.push({ key: "oneWay", price: oneWay.choice.price, unit: oneWay.choice.label.trim() });
    }
    if (branchCount > 0) facts.push({ key: "branches", count: branchCount, campsite: false });
    // The bike block sits inside the vehicle table, so no minimum.
    return facts;
  }

  if (category === "camping") {
    const size = resolvePartySize(product.tags, product.name);
    if (size) facts.push({ key: "party", size });
    const sections = parseComponentSections(
      product.detail_sections,
      disputedComponentNames(product.specifications),
    );
    const common = commonComponentItems(sections);
    if (common.length > 0) {
      const seasonal = sections.some((s) => s.items.length !== common.length);
      facts.push({ key: "components", items: common.slice(0, MAX_ITEMS), seasonal });
    } else {
      const items = inclusionItems(product.inclusions);
      if (items.length > 0) facts.push({ key: "components", items, seasonal: false });
    }
    if (pickup === "campsite") facts.push({ key: "pickup", mode: "campsite" });
    if (branchCount > 1) {
      facts.push({ key: "branches", count: branchCount, campsite: pickup === "campsite" });
    }
  } else {
    const items = inclusionItems(product.inclusions);
    if (items.length > 0) facts.push({ key: "components", items, seasonal: false });
    const names = [...new Set((product.branches ?? []).map((b) => b.name?.trim() ?? ""))].filter(Boolean);
    if (names.length > 0) {
      facts.push({ key: "places", names: names.slice(0, MAX_PLACES), total: names.length });
    }
  }
  return facts.length >= MIN_BLOCK_ROWS ? facts : [];
}

// ---------------------------------------------------------------------------
// Format
// ---------------------------------------------------------------------------

export interface KeyFactRow {
  key: KeyFactKey;
  label: string;
  value: string;
}

/** `useTranslations("product.keyFacts")` / `getTranslations(…)` — the minimal shape. */
export type KeyFactTranslator = (
  key: string,
  values?: Record<string, string | number>,
) => string;

const LABEL_KEYS: Record<KeyFactKey, string> = {
  license: "labels.license",
  mileage: "labels.mileage",
  pickup: "labels.pickup",
  oneWay: "labels.oneWay",
  branches: "labels.branches",
  party: "labels.party",
  components: "labels.components",
  places: "labels.places",
};

const ITEM_JOINER = " · ";

export function formatKeyFactValue(
  fact: KeyFact,
  t: KeyFactTranslator,
  formatPrice: (amount: number) => string,
  locale: string,
): string {
  switch (fact.key) {
    case "license":
      // 모델 허브와 같은 문구(bike-classes.bikeLicenseText)
      return bikeLicenseText(fact.classKey, locale) ?? t(`license.${fact.classKey}`);
    case "mileage":
      return t("values.mileage", { km: fact.km });
    case "pickup":
      return t(`values.pickup.${fact.mode}`);
    case "oneWay":
      return t("values.oneWay", { price: formatPrice(fact.price), unit: fact.unit });
    case "branches":
      return t(fact.campsite ? "values.campsites" : "values.branches", { count: fact.count });
    case "party":
      return t("values.party", { count: fact.size });
    case "components": {
      const items = fact.items.join(ITEM_JOINER);
      return fact.seasonal ? t("values.componentsSeasonal", { items }) : items;
    }
    case "places": {
      const names = fact.names.join(ITEM_JOINER);
      const more = fact.total - fact.names.length;
      return more > 0 ? t("values.placesMore", { names, count: more }) : names;
    }
  }
}

export function formatKeyFacts(
  facts: readonly KeyFact[],
  t: KeyFactTranslator,
  formatPrice: (amount: number) => string,
  locale: string,
): KeyFactRow[] {
  return facts.map((fact) => ({
    key: fact.key,
    label: fact.key === "branches" && fact.campsite ? t("labels.campsites") : t(LABEL_KEYS[fact.key]),
    value: formatKeyFactValue(fact, t, formatPrice, locale),
  }));
}
